import type { Href } from "expo-router";

/** The Patriot backfill page's segment, which the landing page offers only behind its flag. */
export const BACKFILL_SUBPATH = "backfill";

/** The Recompute incidents page's segment, which the landing page offers only behind its flag. */
export const RECOMPUTE_INCIDENTS_SUBPATH = "recompute-incidents";

/** The subtree every Patriot tools page lives under, below the tenant. */
export const PATRIOT_TOOLS_SEGMENT = "monitoring";

export function patriotToolsHref(
  tenantId: string,
  subpath = "",
  query: Record<string, string | undefined> = {},
  anchor?: string,
): Href {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== "") params.set(key, value);
  }
  const search = params.toString();
  const path = `/tenants/${tenantId}/${PATRIOT_TOOLS_SEGMENT}${subpath ? `/${subpath}` : ""}`;
  const fragment = anchor !== undefined ? `#${anchor}` : "";
  // Typed routes cannot see a path assembled at runtime.
  return `${search ? `${path}?${search}` : path}${fragment}` as Href;
}

/** The anchor of one operator note on its activation's page. */
export const operatorNoteAnchor = (noteId: string): string => `operator-note-${noteId}`;

/**
 * Where an activation opens: its tenant's own page when the directory holds the
 * tenant, otherwise the fleet page, which serves a ticket whichever tenant — or
 * none — it belongs to. `anchor` opens it at one part of the page, such as
 * {@link operatorNoteAnchor}.
 */
export function activationHref(
  tenantId: string,
  ticketNo: string,
  knownOwner: string | undefined,
  anchor?: string,
): Href {
  const ticket = encodeURIComponent(ticketNo);
  return knownOwner === undefined ?
      patriotToolsHref(tenantId, `fleet-activations/${ticket}`, {}, anchor)
    : patriotToolsHref(knownOwner, `tenant-activations/${ticket}`, {}, anchor);
}

export const FLEET_CLIENTS_SUBPATH = "fleet-clients";
export const FLEET_ACTIVATIONS_SUBPATH = "fleet-activations";
export const TENANT_INCIDENTS_SUBPATH = "tenant-incidents";
export const FLEET_INCIDENTS_SUBPATH = "fleet-incidents";
export const TENANT_INCIDENT_TOTALS_SUBPATH = "tenant-incident-totals";
export const FLEET_INCIDENT_TOTALS_SUBPATH = "fleet-incident-totals";

/**
 * Where an incident opens: its tenant's own page when the directory holds the
 * tenant, otherwise the fleet page, as for an activation.
 */
export function incidentHref(tenantId: string, incidentId: string, knownOwner: string | undefined): Href {
  const id = encodeURIComponent(incidentId);
  return knownOwner === undefined ?
      patriotToolsHref(tenantId, `${FLEET_INCIDENTS_SUBPATH}/${id}`)
    : patriotToolsHref(knownOwner, `${TENANT_INCIDENTS_SUBPATH}/${id}`);
}

/** The wall clock `timeZone` shows at `at`, to the minute: `YYYY-MM-DDTHH:mm`, as the list filters read it. */
function wallClockIn(at: Date, timeZone: string): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(at)
      .map((part) => [part.type, part.value]),
  );
  return `${parts["year"] ?? ""}-${parts["month"] ?? ""}-${parts["day"] ?? ""}T${parts["hour"] ?? ""}:${parts["minute"] ?? ""}`;
}

interface EndingAt {
  readonly at: string;
  readonly timeZone: string;
}

const endingOn = (endingAt: EndingAt | undefined) =>
  endingAt !== undefined && {
    to: wallClockIn(new Date(endingAt.at), endingAt.timeZone).slice(0, 10),
    tz: endingAt.timeZone,
  };

/**
 * One client's incidents on the fleet page, over the default window — ending on
 * the day `endingAt.at` falls on in its `timeZone` when given, so the incident
 * it was opened from is listed; otherwise ending today in the viewer's zone.
 */
export function clientIncidentsHref(tenantId: string, clientNo: string, endingAt?: EndingAt): Href {
  return patriotToolsHref(tenantId, FLEET_INCIDENTS_SUBPATH, { clientNo, ...endingOn(endingAt) });
}

/**
 * The organisation's incidents, optionally narrowed to a site or a camera at
 * it, over the window {@link clientIncidentsHref} takes. The list filters a
 * camera only within its site, so a camera needs its `site` too.
 */
export function tenantIncidentsHref(
  tenantId: string,
  scope: { readonly site?: string; readonly camera?: string },
  endingAt?: EndingAt,
): Href {
  return patriotToolsHref(tenantId, TENANT_INCIDENTS_SUBPATH, { ...scope, ...endingOn(endingAt) });
}

export const FLEET_OPERATOR_NOTES_SUBPATH = "fleet-operator-notes";
export const FLEET_SIGNALS_SUBPATH = "fleet-signals";
export const TENANT_SIGNALS_SUBPATH = "tenant-signals";

/**
 * Where a signal opens, by Patriot's id: its tenant's own page when the
 * directory holds the tenant, otherwise the fleet page, as for an activation.
 */
export function signalHref(tenantId: string, signalId: string, knownOwner: string | undefined): Href {
  const id = encodeURIComponent(signalId);
  return knownOwner === undefined ?
      patriotToolsHref(tenantId, `${FLEET_SIGNALS_SUBPATH}/${id}`)
    : patriotToolsHref(knownOwner, `${TENANT_SIGNALS_SUBPATH}/${id}`);
}

/**
 * A client's operator notes in the minutes around `at`: where a call made
 * outside any activation can be read, since no activation page holds its note.
 */
export function operatorNotesAroundHref(tenantId: string, clientNo: string, at: string, timeZone: string): Href {
  const instant = Date.parse(at);
  return patriotToolsHref(tenantId, FLEET_OPERATOR_NOTES_SUBPATH, {
    clientNo,
    from: wallClockIn(new Date(instant - 60_000), timeZone),
    to: wallClockIn(new Date(instant + 60_000), timeZone),
    tz: timeZone,
  });
}

/** A client's page, at one of its versions or `latest`; absent, the page says `latest` itself. */
export function fleetClientHref(tenantId: string, clientNo: string, version?: string): Href {
  return patriotToolsHref(tenantId, `${FLEET_CLIENTS_SUBPATH}/${encodeURIComponent(clientNo)}`, { version });
}

// Every fleet list matches `?clientNo=` as a prefix; a whole client number is
// a prefix of no other, so it finds just that client.

export function clientActivationsHref(tenantId: string, clientNo: string): Href {
  return patriotToolsHref(tenantId, FLEET_ACTIVATIONS_SUBPATH, { clientNo });
}

export function clientSignalsHref(tenantId: string, clientNo: string): Href {
  return patriotToolsHref(tenantId, FLEET_SIGNALS_SUBPATH, { clientNo });
}

export function clientOperatorNotesHref(tenantId: string, clientNo: string): Href {
  return patriotToolsHref(tenantId, FLEET_OPERATOR_NOTES_SUBPATH, { clientNo });
}

export interface PatriotTool {
  readonly subpath: string;
  readonly title: string;
  readonly description: string;
}

export interface PatriotToolSection {
  readonly title: string;
  /** Its title names the scope its pages' titles leave out, so it prefixes each page's crumb. */
  readonly scoped?: boolean;
  /**
   * Its pages are the tenant's own, so the tenant's name stands for its title,
   * both on the landing page and in each page's crumb.
   */
  readonly ofTenant?: boolean;
  readonly tools: readonly PatriotTool[];
}

/** The pages listed on the Patriot tools landing page, by section. Add a page here to list it. */
export const PATRIOT_TOOL_SECTIONS: readonly PatriotToolSection[] = [
  {
    title: "This organisation",
    scoped: true,
    ofTenant: true,
    tools: [
      {
        subpath: TENANT_INCIDENT_TOTALS_SUBPATH,
        title: "Incident summary",
        description: "Genuine incidents and activations, totalled by day, week or month",
      },
      {
        subpath: TENANT_INCIDENTS_SUBPATH,
        title: "Genuine incidents",
        description: "Incidents an operator acted on with a phone call",
      },
      {
        subpath: "tenant-activations",
        title: "Patriot activations",
        description: "Cases Patriot raised for an operator to attend",
      },
      {
        subpath: TENANT_SIGNALS_SUBPATH,
        title: "Patriot signals",
        description: "Signals Patriot received from a3s",
      },
    ],
  },
  {
    title: "Vision Intelligence fleet",
    scoped: true,
    tools: [
      {
        subpath: FLEET_INCIDENT_TOTALS_SUBPATH,
        title: "Incident summary",
        description: "Genuine incidents and activations, totalled by day, week or month",
      },
      {
        subpath: FLEET_INCIDENTS_SUBPATH,
        title: "Genuine incidents",
        description: "Incidents an operator acted on with a phone call",
      },
      {
        subpath: FLEET_ACTIVATIONS_SUBPATH,
        title: "Patriot activations",
        description: "Cases Patriot raised for an operator to attend, including ones with no a3s signal",
      },
      {
        subpath: FLEET_SIGNALS_SUBPATH,
        title: "Patriot signals",
        description: "Signals Patriot received from any sender, excluding Patriot's internal housekeeping signals",
      },
      {
        subpath: FLEET_CLIENTS_SUBPATH,
        title: "Patriot clients",
        description: "Accounts Patriot monitors, one per asset",
      },
      {
        subpath: FLEET_OPERATOR_NOTES_SUBPATH,
        title: "Patriot operator notes",
        description: "Notes Patriot recorded against activations and clients",
      },
    ],
  },
  {
    title: "Commands",
    tools: [
      {
        subpath: "connection-check",
        title: "Patriot connection check",
        description: "Check that a3s can reach Patriot and read its data",
      },
      {
        subpath: BACKFILL_SUBPATH,
        title: "Patriot backfill",
        description: "Copy Patriot records into a3s over a range of time",
      },
      {
        subpath: RECOMPUTE_INCIDENTS_SUBPATH,
        title: "Recompute incidents",
        description: "Count genuine incidents again over whole days",
      },
    ],
  },
];
